import { AnalyticIdentifier, GitHubAppInstallQueryField } from '@common/neo.tax';
import { colors, ModalHeader, RadioGroupCards } from '@ui-components/neo.tax';
import React, { useState } from 'react';

import { GitHubConnectMethod } from '@neo.tax/shared/enums';
import { TicketingProvider } from '@neo.tax/shared/graphql';
import { useCurrentCompany, useUrlQueryParam } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';

import { StyledConnectGitHub, StyledGitHubBody } from './connect-github.styled';
import { StyledAutomationsIcon } from './connect-provider.styled';
import { ConnectGitHubApp } from './ConnectGitHubApp';
import { ConnectTicketing } from './ConnectTicketing';
import { MultipleActions } from './MultipleActions';

interface ConnectGitHubProps {
  providerName: TicketingProvider;
  close: () => void;
  restart: () => void;
  /**
   * Opens straight on one method's slide instead of asking. Set by the Data Sources connection rows,
   * where the customer has already chosen by clicking "Migrate to GitHub App" or "Relink" on a
   * specific connection — asking again would be asking them to repeat themselves.
   *
   * Deliberately not the install-return signal: that one also puts `ConnectGitHubApp` into its
   * polling view, which is wrong for a migration where no redirect is in flight.
   */
  initialMethod?: GitHubConnectMethod | null;
  /** Passed to the App slide; see its `initialOrganizationSlug`. */
  initialOrganizationSlug?: string | null;
}

/**
 * Entry point for connecting GitHub. GitHub is the one ticketing provider with two acquisition
 * methods, so it gets a method-choice step ahead of the provider-specific slide (the other
 * providers go straight to their Merge flow). Returning from the GitHub App install redirect skips
 * the choice — the user already committed to the App and only needs the poll/finish view.
 */
export const ConnectGitHub = ({
  close,
  initialMethod,
  initialOrganizationSlug,
  providerName,
  restart,
}: ConnectGitHubProps): React.ReactElement => {
  const isReturningFromInstall = useUrlQueryParam(GitHubAppInstallQueryField.CONNECTED_STATUS) !== null;
  // Environments with no GitHub App configured (single-tenant deployments, demo, sandbox,
  // production-preview) cannot complete an install: buildInstallUrl fails fast and the user lands on
  // a generic error. Offering the choice there is a dead option, so those environments go straight
  // to the Merge path as if GitHub had only ever had one connection method.
  const { company } = useCurrentCompany();
  const gitHubAppAvailable = company?.gitHubAppAvailable ?? false;

  const [method, setMethod] = useState<GitHubConnectMethod | null>(
    isReturningFromInstall ? GitHubConnectMethod.GITHUB_APP : (initialMethod ?? null),
  );
  const [selection, setSelection] = useState<GitHubConnectMethod>(initialMethod ?? GitHubConnectMethod.GITHUB_APP);

  // Distinct identifiers from the slides this routes to: useAnalyticId registers each id in a
  // global registry and throws RepeatedAnalyticsIdError if two mounted components claim the same
  // one. Choosing a method mounts the next slide while this one is still tearing down, so sharing
  // LINK_TICKETING* with ConnectGitHubApp/ConnectTicketing crashes the app on Continue.
  const cancelConnectionId = useAnalyticId(AnalyticIdentifier.LINK_GITHUB_METHOD_CANCEL);
  const continueId = useAnalyticId(AnalyticIdentifier.LINK_GITHUB_METHOD_CONTINUE);

  // "Back" out of a chosen method returns to this choice rather than all the way to the provider
  // picker, so switching methods doesn't cost the user the whole selection flow. A customer who
  // arrived on a method they already chose gets the picker they never saw, which is still the
  // nearest thing to "back".
  const backToMethodChoice = (): void => setMethod(null);

  // Placed below every hook call so it stays an unconditional-hooks component.
  //
  // Only the *choice* collapses to Merge where no App is configured. A caller that asked for the
  // App by name is not offering a choice — silently landing that customer in the personal-access-
  // token flow would connect them the way they were trying to move away from. Keyed on what the
  // caller asked for rather than on the current method, so backing out of the App slide reaches the
  // method picker instead of re-satisfying this and diverting them after all.
  const appRequestedExplicitly = initialMethod === GitHubConnectMethod.GITHUB_APP;
  if (!gitHubAppAvailable && !isReturningFromInstall && !appRequestedExplicitly) {
    return <ConnectTicketing close={close} restart={restart} providerName={providerName} />;
  }

  if (method === GitHubConnectMethod.GITHUB_APP) {
    return (
      <ConnectGitHubApp
        close={close}
        restart={backToMethodChoice}
        providerName={providerName}
        initialOrganizationSlug={initialOrganizationSlug}
      />
    );
  }

  if (method === GitHubConnectMethod.MERGE_DEV) {
    return <ConnectTicketing close={close} restart={backToMethodChoice} providerName={providerName} />;
  }

  return (
    <StyledConnectGitHub>
      <ModalHeader
        copy="Select how you want to connect GitHub."
        icon={<StyledAutomationsIcon />}
        iconBgColor={colors.blue[50]}
        subtitle="Step 1 of 2"
        title="Connect GitHub"
      />
      <StyledGitHubBody direction="column">
        <RadioGroupCards<GitHubConnectMethod>
          id="github-connect-method"
          label="Connection method"
          value={selection}
          onChange={setSelection}
          options={[
            {
              id: 'github-connect-method-app',
              value: GitHubConnectMethod.GITHUB_APP,
              label: 'Install the Neo.Tax GitHub App (recommended)',
              description: 'Owned by your organization. Keeps working when people leave.',
            },
            {
              id: 'github-connect-method-merge',
              value: GitHubConnectMethod.MERGE_DEV,
              label: 'Connect with a personal access token',
              description: 'Tied to one person’s account. Breaks if their token is revoked.',
            },
          ]}
        />
      </StyledGitHubBody>
      <MultipleActions
        actions={[
          {
            button: {
              variant: 'white',
              rounded: 'regular',
              size: 'base',
              onClick: restart,
              children: 'Cancel and Go Back',
              id: cancelConnectionId,
            },
          },
          {
            button: {
              variant: 'primary',
              rounded: 'regular',
              size: 'base',
              onClick: (): void => setMethod(selection),
              children: 'Continue',
              id: continueId,
            },
          },
        ]}
      />
    </StyledConnectGitHub>
  );
};
